class MetaBuyBox extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-add-to-cart-trigger]');
    this.form = this.querySelector('.meta-buy-box__form form');
    this.status = this.querySelector('[data-meta-cart-status]');
    this.errorSummary = this.querySelector('[data-meta-cart-error]');
    this.sticky = this.querySelector('[data-meta-sticky]');
    this.stickyButton = this.querySelector('[data-meta-sticky-trigger]');
    this.stickyStatus = this.querySelector('[data-meta-sticky-status]');

    if (!this.button || !this.form || !this.status) return;
    if (!this.button.classList.contains('ajax-submit')) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleCartStart = this.handleCartStart.bind(this);
    this.handleCartSuccess = this.handleCartSuccess.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleEnvironmentChange = this.handleEnvironmentChange.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
    this.addEventListener('click', this.handleClick);
    this.form.addEventListener('ajax-cart:add-start', this.handleCartStart);
    this.form.addEventListener('ajax-cart:add-success', this.handleCartSuccess);
    this.form.addEventListener('ajax-cart:add-error', this.handleCartError);
    this.setupStickyView();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.form?.removeEventListener('ajax-cart:add-start', this.handleCartStart);
    this.form?.removeEventListener('ajax-cart:add-success', this.handleCartSuccess);
    this.form?.removeEventListener('ajax-cart:add-error', this.handleCartError);
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.mobileMedia?.removeEventListener?.('change', this.handleEnvironmentChange);
    globalThis.removeEventListener?.('resize', this.handleEnvironmentChange);
    globalThis.removeEventListener?.('orientationchange', this.handleEnvironmentChange);
    globalThis.visualViewport?.removeEventListener?.('resize', this.handleEnvironmentChange);
    document.removeEventListener?.('focusin', this.handleEnvironmentChange);
    document.removeEventListener?.('focusout', this.handleFocusOut);

    if (this.mutationFrame != null) {
      globalThis.cancelAnimationFrame?.(this.mutationFrame);
      this.mutationFrame = null;
    }
    if (this.focusoutTimer != null) {
      globalThis.clearTimeout?.(this.focusoutTimer);
      this.focusoutTimer = null;
    }
  }

  handleClick(event) {
    const stickyButton = event.target.closest('[data-meta-sticky-trigger]');
    if (stickyButton && this.contains(stickyButton)) {
      event.preventDefault?.();
      if (stickyButton.disabled || this.button.disabled || this.isAdding) return;

      this.button.click();
      return;
    }

    const retry = event.target.closest('[data-meta-cart-retry]');
    if (retry && this.contains(retry)) {
      this.button.focus();
      this.updateSticky();
      return;
    }

    const button = event.target.closest('[data-add-to-cart-trigger]');
    if (!button || !this.contains(button) || button.disabled || this.isAdding) return;

    this.isAdding = true;
    this.requestToken = null;
    this.errorSummary?.setAttribute('hidden', '');
    this.setState('adding');
  }

  handleCartStart(event) {
    if (event.target !== this.form || !this.isAdding) return;

    this.requestToken = event.detail?.requestToken;
  }

  handleCartSuccess(event) {
    if (!this.isOwnCartRequest(event)) return;

    this.isAdding = false;
    this.errorSummary?.setAttribute('hidden', '');
    this.setState('success');
    this.focusCartSummary();
  }

  handleCartError(event) {
    if (!this.isOwnCartRequest(event)) return;

    this.isAdding = false;
    this.status.textContent = '';
    this.errorSummary?.removeAttribute('hidden');
    this.button.disabled = false;
    this.setState('error');
    this.errorSummary?.focus();
  }

  isOwnCartRequest(event) {
    return Boolean(
      this.isAdding
      && event.target === this.form
      && this.requestToken
      && event.detail?.requestToken === this.requestToken,
    );
  }

  setState(state) {
    this.dataset.state = state;

    if (state === 'available') this.status.textContent = this.dataset.stateAvailable;
    if (state === 'adding') this.status.textContent = this.dataset.stateAdding;
    if (state === 'success') this.status.textContent = this.dataset.stateSuccess;
    if (state === 'unavailable') this.status.textContent = this.dataset.stateUnavailable;

    if (this.sticky) this.sticky.dataset.state = state;
    if (this.stickyStatus) this.stickyStatus.textContent = this.stateMessage(state);
    if (this.stickyButton) {
      this.stickyButton.disabled = state === 'adding' || state === 'success' || state === 'unavailable';
      const ariaDisabled = this.stickyButton.disabled ? 'true' : 'false';
      if (this.stickyButton.getAttribute('aria-disabled') !== ariaDisabled) {
        this.stickyButton.setAttribute('aria-disabled', ariaDisabled);
      }
    }

    this.updateSticky();
  }

  stateMessage(state) {
    if (state === 'adding') return this.dataset.stateAdding;
    if (state === 'success') return this.dataset.stateSuccess;
    if (state === 'error') return this.dataset.stateError;
    if (state === 'unavailable') return this.dataset.stateUnavailable;
    return this.dataset.stateAvailable;
  }

  setupStickyView() {
    if (!this.sticky || !this.stickyButton || !this.stickyStatus) return;

    this.originalAboveViewport = false;
    this.footerVisible = false;
    this.mobileMedia = globalThis.matchMedia?.('(max-width: 768px)');
    this.mobileMedia?.addEventListener?.('change', this.handleEnvironmentChange);
    globalThis.addEventListener?.('resize', this.handleEnvironmentChange);
    globalThis.addEventListener?.('orientationchange', this.handleEnvironmentChange);
    globalThis.visualViewport?.addEventListener?.('resize', this.handleEnvironmentChange);
    document.addEventListener?.('focusin', this.handleEnvironmentChange);
    document.addEventListener?.('focusout', this.handleFocusOut);

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0 });
      this.intersectionObserver.observe(this);
      this.footer = document.querySelector('footer, [role="contentinfo"]');
      if (this.footer) this.intersectionObserver.observe(this.footer);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(this.button, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
      if (document.body) {
        this.mutationObserver.observe(document.body, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'hidden', 'aria-hidden'],
        });
      }
    }

    this.setState(this.dataset.state);
  }

  handleIntersection(entries) {
    for (const entry of entries) {
      if (entry.target === this) {
        this.originalAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      } else if (entry.target === this.footer) {
        this.footerVisible = entry.isIntersecting;
      }
    }

    this.updateSticky();
  }

  handleEnvironmentChange() {
    this.syncOriginalAvailability();
    this.updateSticky();
  }

  handleFocusOut() {
    if (this.focusoutTimer != null) return;

    const defer = globalThis.setTimeout || ((callback) => callback());
    this.focusoutTimer = defer(() => {
      this.focusoutTimer = null;
      this.updateSticky();
    }, 0);
  }

  handleMutations() {
    if (this.mutationFrame != null) return;

    const requestFrame = globalThis.requestAnimationFrame || ((callback) => callback());
    this.mutationFrame = requestFrame(() => {
      this.mutationFrame = null;
      this.syncOriginalAvailability();
      this.updateSticky();
    });
  }

  syncOriginalAvailability() {
    if (this.isAdding) return;

    if (this.button.disabled && this.dataset.state !== 'success') {
      this.setState('unavailable');
    } else if (!this.button.disabled && this.dataset.state === 'unavailable') {
      this.setState('available');
    }
  }

  updateSticky() {
    if (!this.sticky) return;

    const shouldShow = Boolean(
      this.originalAboveViewport
      && this.isCompatibleViewport()
      && !this.footerVisible
      && !this.isInterfaceObstructed(),
    );

    if (this.sticky.hidden === shouldShow) this.sticky.hidden = !shouldShow;
    this.sticky.classList.toggle('is-visible', shouldShow);
    const ariaHidden = shouldShow ? 'false' : 'true';
    if (this.sticky.getAttribute('aria-hidden') !== ariaHidden) {
      this.sticky.setAttribute('aria-hidden', ariaHidden);
    }
  }

  isCompatibleViewport() {
    if (!this.mobileMedia?.matches) return false;

    const viewportHeight = globalThis.visualViewport?.height || globalThis.innerHeight;
    return !viewportHeight || viewportHeight >= 448;
  }

  isInterfaceObstructed() {
    const activeElement = document.activeElement;
    if (this.errorSummary && activeElement && (
      activeElement === this.errorSummary || this.errorSummary.contains?.(activeElement)
    )) return true;

    if (activeElement) {
      const tagName = activeElement.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || activeElement.isContentEditable) {
        return true;
      }
    }

    const fullHeight = globalThis.innerHeight;
    const visibleHeight = globalThis.visualViewport?.height;
    if (fullHeight && visibleHeight) {
      const keyboardOffset = fullHeight - visibleHeight;
      if (keyboardOffset > Math.max(150, fullHeight * 0.2)) return true;
    }

    return this.hasVisibleSurface(
      '[data-ajax-cart-drawer].is-visible, .theme-ajax-cart.is-drawer.is-visible, .ajax-cart__overlay.is-visible',
    ) || this.hasVisibleSurface(
      '#shopify-pc__banner, [data-shopify-privacy-banner], #shopify-section-fixed-message, [id*="truevault"], [class*="truevault"], [id*="polaris"], [class*="polaris"]',
    );
  }

  hasVisibleSurface(selector) {
    const surfaces = document.querySelectorAll?.(selector) || [];
    const viewportHeight = globalThis.visualViewport?.height || globalThis.innerHeight;
    for (const surface of surfaces) {
      if (surface.hidden || surface.getAttribute?.('aria-hidden') === 'true') continue;

      const style = globalThis.getComputedStyle?.(surface);
      if (style && (style.display === 'none' || style.visibility === 'hidden')) continue;

      const bounds = surface.getBoundingClientRect?.();
      if (!bounds || (
        bounds.width > 0
        && bounds.height > 0
        && bounds.bottom > 0
        && (!viewportHeight || bounds.top < viewportHeight)
      )) return true;
    }

    return false;
  }

  focusCartSummary() {
    const cartSummary = document.querySelector('[data-ajax-cart-content] .ajax-cart__cart-title');
    if (!cartSummary) return;

    cartSummary.setAttribute('tabindex', '-1');
    cartSummary.focus();
  }
}

if (!customElements.get('meta-buy-box')) {
  customElements.define('meta-buy-box', MetaBuyBox);
}
